// packages/web/src/components/teach/ExerciseLibraryTable.tsx
import React, { useState } from 'react';
import { useExerciseLibraryStore } from '../../stores/exerciseLibraryStore';
import { Eye, Trash2, CheckSquare, Square } from 'lucide-react';
import { MathRenderer } from '../common/MathRenderer.v3';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

export const ExerciseLibraryTable: React.FC = () => {
  const {
    groupByTimeline,
    toggleExerciseSelection,
    updateExerciseRequirement,
    deleteExercises,
    setEditingRequirement,
    setSelectedExerciseForDetail,
  } = useExerciseLibraryStore();

  const timelineGroups = groupByTimeline();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; isAssigned: boolean } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toast, success, error, warning, hideToast } = useToast();

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      math: '数学',
      physics: '物理',
      chemistry: '化学',
      others: '其他',
    };
    return labels[category] || category;
  };

  const handleStartEdit = (id: string, currentValue: string) => {
    setEditingId(id);
    setEditingValue(currentValue || '');
    setEditingRequirement(id, true);
  };

  const handleSaveEdit = async (id: string) => {
    try {
      await updateExerciseRequirement(id, editingValue);
      setEditingId(null);
      setEditingRequirement(id, false);
      success('保存成功');
    } catch (err) {
      error('保存失败');
    }
  };

  const handleCancelEdit = (id: string) => {
    setEditingId(null);
    setEditingRequirement(id, false);
  };

  const handleDelete = (id: string, isAssigned: boolean) => {
    if (isAssigned) {
      warning('已布置的习题不能删除');
      return;
    }
    setDeleteConfirm({ id, isAssigned });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      await deleteExercises([deleteConfirm.id]);
      success('删除成功');
      setDeleteConfirm(null);
    } catch (err) {
      error('删除失败');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {timelineGroups.map((group) => (
        <div key={group.date} className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* 时间线标题 */}
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <h4 className="text-sm font-medium text-gray-700">{group.label}</h4>
          </div>

          {/* 表格 */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3 text-left">
                    <span className="sr-only">选择</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    标题
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    科目
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    教学要求
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {group.exercises.map((exercise) => (
                  <tr key={exercise.id} className="hover:bg-gray-50">
                    {/* 复选框 */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleExerciseSelection(exercise.id)}
                        className="text-gray-400 hover:text-blue-600"
                      >
                        {exercise.selected ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>

                    {/* 标题 */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={exercise.title}>
                        {exercise.title}
                      </div>
                    </td>

                    {/* 科目 */}
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getCategoryLabel(exercise.category)}
                      </span>
                    </td>

                    {/* 教学要求 - 内联编辑 */}
                    <td className="px-4 py-3">
                      {editingId === exercise.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => handleSaveEdit(exercise.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(exercise.id);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit(exercise.id);
                              }
                            }}
                            autoFocus
                            className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      ) : (
                        <div
                          onClick={() => handleStartEdit(exercise.id, exercise.teacher_requirement || '')}
                          className="text-sm text-gray-600 max-w-xs truncate cursor-pointer hover:text-blue-600"
                          title={exercise.teacher_requirement || '点击编辑'}
                        >
                          {exercise.teacher_requirement || (
                            <span className="text-gray-400 italic">点击编辑</span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* 创建时间 */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500">
                        {new Date(exercise.created_at).toLocaleString('zh-CN', {
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </td>

                    {/* 布置状态 */}
                    <td className="px-4 py-3">
                      {exercise.is_assigned ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          已布置 ({exercise.assignment_count})
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          未布置
                        </span>
                      )}
                    </td>

                    {/* 操作 */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setSelectedExerciseForDetail(exercise)}
                          className="text-blue-600 hover:text-blue-800"
                          title="查看详情"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {!exercise.is_assigned && (
                          <button
                            onClick={() => handleDelete(exercise.id, exercise.is_assigned)}
                            className="text-red-600 hover:text-red-800"
                            title="删除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* 删除确认对话框 */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="确认删除"
        message="确定要删除这个习题吗？删除后无法恢复。"
        confirmText="删除"
        cancelText="取消"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
        isLoading={deleting}
        type="danger"
      />

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
